<template>
  <div class="sr-card">
    <div class="sr-card__head">
      <div>
        <div class="sr-card__title">{{ $t("salesReceiptsTitle") }}</div>
        <div class="sr-card__legend">
          <span class="sr-card__dot sr-card__dot--sell" />
          {{ $t("sell") }}
          <span class="sr-card__dot sr-card__dot--receive" />
          {{ $t("received") }}
          <span class="sr-card__dot sr-card__dot--remain" />
          {{ $t("uncollectedRemain") }}
        </div>
      </div>
      <div v-if="periodLabel" class="sr-card__period">
        {{ periodLabel }}
      </div>
    </div>
    <div v-if="canSee" class="sr-card__body">
      <apexchart
        v-if="hasData"
        :key="chartKey"
        type="line"
        height="248"
        :options="chartOptions"
        :series="series"
      />
      <div v-else class="sr-card__empty">
        <v-progress-circular
          v-if="loading"
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
        <home-empty-state v-else />
      </div>
      <div v-if="hasData && insight" class="sr-card__insight">
        {{ insight }}
      </div>
    </div>
    <div v-else class="sr-card__empty">
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from "./HomeEmptyState.vue";

export default {
  name: "HomeSalesReceipts",
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: "7days",
    },
    periodLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      items: [],
      loading: false,
    };
  },
  computed: {
    canSee() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("overview")
      );
    },
    rows() {
      return this.items.map((item) => {
        const sell = parseFloat(item.sell) || 0;
        const receive = parseFloat(item.receive) || 0;
        return {
          key: item.key,
          sell,
          receive,
          remain: Math.max(0, sell - receive),
        };
      });
    },
    hasData() {
      return this.rows.some((row) => row.sell > 0 || row.receive > 0);
    },
    chartKey() {
      return this.dateType + "-" + this.rows.length;
    },
    series() {
      return [
        {
          name: this.$t("sell"),
          type: "column",
          data: this.rows.map((row) => row.sell),
        },
        {
          name: this.$t("received"),
          type: "column",
          data: this.rows.map((row) => row.receive),
        },
        {
          name: this.$t("uncollectedRemain"),
          type: "line",
          data: this.rows.map((row) => row.remain),
        },
      ];
    },
    insight() {
      if (!this.rows.length) return "";
      const worst = this.rows.reduce((best, row) =>
        row.remain > best.remain ? row : best
      );
      if (worst.remain <= 0) {
        return this.$t("fullyCollected");
      }
      return (
        this.$t("salesReceiptsWorstGap") +
        " " +
        worst.key +
        " · " +
        this.$numberFormat(worst.remain) +
        " " +
        (this.$store.state.currency.name || "")
      );
    },
    chartOptions() {
      const self = this;
      return {
        chart: {
          type: "line",
          fontFamily: "IRANSans",
          toolbar: { show: false },
          zoom: { enabled: false },
          animations: { enabled: false },
          stacked: false,
        },
        colors: ["#0066FF", "#00CAE3", "#E7000B"],
        stroke: {
          width: [0, 0, 2],
          curve: "smooth",
        },
        plotOptions: {
          bar: {
            columnWidth: this.rows.length > 8 ? "72%" : "46%",
            borderRadius: 3,
            borderRadiusApplication: "end",
          },
        },
        dataLabels: { enabled: false },
        markers: {
          size: this.rows.length > 12 ? 0 : 4,
          colors: ["#E7000B"],
          strokeColors: "#fff",
          strokeWidth: 2,
        },
        fill: { opacity: [0.95, 0.95, 1] },
        grid: {
          borderColor: "#E5E7EB",
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        legend: { show: false },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter(value) {
              return (
                self.$numberFormat(value || 0) +
                " " +
                (self.$store.state.currency.name || "")
              );
            },
          },
        },
        xaxis: {
          categories: this.rows.map((row) => row.key),
          tickAmount: this.rows.length > 12 ? 7 : undefined,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: {
            rotate: 0,
            hideOverlappingLabels: true,
            style: { fontSize: "10px", colors: "#6B7280" },
          },
        },
        yaxis: {
          opposite: !!this.$vuetify.rtl,
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { fontSize: "10px", colors: "#6B7280" },
            offsetX: this.$vuetify.rtl ? -10 : 0,
            formatter(value) {
              return self.$numberFormat(value);
            },
          },
        },
      };
    },
  },
  watch: {
    dateType() {
      this.fetchList();
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      this.loading = true;
      this.$axios
        .get(this.$endpoint + "widgets", {
          params: {
            type: "CompareSellByReceive",
            dateType: this.dateType,
          },
        })
        .then((res) => {
          this.items = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.sr-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px 14px 10px;
  height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.sr-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  margin-bottom: 4px;
}
.sr-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.sr-card__legend {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 6px;
  font-size: 11px;
  color: #6b7280;
}
.sr-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.sr-card__dot--sell {
  background: #0066ff;
}
.sr-card__dot--receive {
  background: #00cae3;
}
.sr-card__dot--remain {
  background: #e7000b;
}
.sr-card__period {
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.sr-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.sr-card__insight {
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.5;
}
.sr-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}
</style>
